import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useContentRibbons from '../../../core/apiHooks/useContentRibbons';
import useSubscriptionPackages from '../../../core/apiHooks/useSubscriptionPackages';
import useTags from '../../../core/apiHooks/useTags';
import { DocumentTypesEnum } from '../../../core/enums/PublicationTypesEnum';
import { useDebouncedCallback } from '../../../core/hooks/useDebouncedCallback';
import { CountriesEnum, GlobalContext } from '../../../GlobalContext';
import { TagsSelectType } from '../../../components/Filters/Filters';
import { getSortTypes, SortTypesEnum } from '../../../components/Filters/Filters.helpers';
import FiltersBar from './components/FiltersBar';
import TagsBar from './components/TagsBar';
import './BrowseSection.styles.scss';
import { MAC_CATEGORY_TITLE } from '../../../core/api';

const TYPE_OPTIONS = [
    { id: DocumentTypesEnum.Publication, title: 'Publication' },
    { id: DocumentTypesEnum.Mac, title: 'MAC' },
    { id: DocumentTypesEnum.Chapter, title: 'Chapter' }
];

interface Props {
    filters: {
        ribbonsIds: number[];
        categoriesIds: number[];
        subscriptionPackageIds: number[];
        tagsIds: number[];
        orderBy: string;
        sortOrder: string;
        hasAccess: boolean;
        types: string[];
        searchQuery?: string;
    };
    shouldShowFilteredResults: boolean;
    onChange: (next: Props['filters']) => void;
    viewMode?: 'tile' | 'table';
    onViewModeChange?: (m: 'tile' | 'table') => void;
}

const BrowseSection: React.FC<Props> = ({
    filters,
    onChange,
    viewMode = 'tile',
    onViewModeChange,
    shouldShowFilteredResults
}) => {
    const {
        getRem,
        selectedCountryId,
        currentUser: { globalAssociatedRibbonsIds }
    } = useContext(GlobalContext);
    const { inMyLibrary } = useContext(GlobalContext);
    const [mode, setMode] = useState<'tile' | 'table'>(
        () => (localStorage.getItem('browseSection:viewMode') as 'tile' | 'table') || viewMode
    );
    const [localSearchQuery, setLocalSearchQuery] = useState(filters.searchQuery ?? '');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const shouldRetainFocusRef = useRef(false);
    const isUserInputRef = useRef(false);

    const debouncedSearchChange = useDebouncedCallback(
        (value: string) => {
            if (filters.searchQuery !== value) {
                onChange({ ...filters, searchQuery: value });
            }
        },
        500,
        [filters, onChange]
    );

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            isUserInputRef.current = true;
            shouldRetainFocusRef.current = true;
            setLocalSearchQuery(value);
            debouncedSearchChange(value);
        },
        [debouncedSearchChange]
    );

    const handleSearchClear = useCallback(() => {
        isUserInputRef.current = true;
        shouldRetainFocusRef.current = true;
        setLocalSearchQuery('');
        onChange({ ...filters, searchQuery: '' });
    }, [filters, onChange]);

    const handleSearchBlur = useCallback(() => {
        shouldRetainFocusRef.current = false;
    }, []);

    useEffect(() => {
        if (shouldRetainFocusRef.current && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    });

    useEffect(() => {
        if (!isUserInputRef.current && filters.searchQuery !== localSearchQuery) {
            setLocalSearchQuery(filters.searchQuery ?? '');
        }
        isUserInputRef.current = false;
    }, [filters.searchQuery, localSearchQuery]);

    const getSortTypeFromFilters = (orderBy: string, sortOrder: string): SortTypesEnum => {
        const foundSort = Object.values(SortTypesEnum).find((sortType) => {
            if (typeof sortType === 'number') {
                const { direction, sortBy } = getSortTypes(sortType);
                return direction === sortOrder && sortBy === orderBy;
            }
            return false;
        }) as SortTypesEnum;
        return foundSort || SortTypesEnum.DateDesc;
    };

    const [selectedSortType, setSelectedSortType] = useState<SortTypesEnum[]>(() => [
        getSortTypeFromFilters(filters.orderBy, filters.sortOrder)
    ]);

    useEffect(() => {
        const newSortType = getSortTypeFromFilters(filters.orderBy, filters.sortOrder);
        setSelectedSortType((prev) => {
            if (prev[0] !== newSortType) {
                return [newSortType];
            }
            return prev;
        });
    }, [filters.orderBy, filters.sortOrder]);

    useEffect(() => {
        if (viewMode && viewMode !== mode) {
            setMode(viewMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    useEffect(() => {
        localStorage.setItem('browseSection:viewMode', mode);
    }, [mode]);

    const { data: ribbons, isLoading: catLoad } = useContentRibbons(inMyLibrary);
    const NEW_RELEASE_RIBBON_ID = 18;
    const extraRibbonsForTags = useMemo(() => {
        if (selectedCountryId === CountriesEnum.Global) {
            return globalAssociatedRibbonsIds;
        }

        return [];
    }, [selectedCountryId, globalAssociatedRibbonsIds]);
    const activeRibbonIdForTags = filters.ribbonsIds.includes(NEW_RELEASE_RIBBON_ID) ? undefined : filters.ribbonsIds[0];

    const ribbonsForTags = useMemo(() => {
        const result = [...extraRibbonsForTags];
        if (activeRibbonIdForTags) {
            result.push(activeRibbonIdForTags);
        }

        if (result.length === 0) return undefined;
        return result;
    }, [activeRibbonIdForTags, extraRibbonsForTags]);

    const { data: tags, isLoading: tagLoad } = useTags(ribbonsForTags);
    const { data: subscriptionPackages, isLoading: packageLoad } = useSubscriptionPackages();
    const isTagsDropdownDisabled = !tagLoad && (tags?.length ?? 0) === 0;

    const ribbonOptions = ribbons?.map((r) => ({
        id: r.id,
        title: r.title
    }));

    const hasFilters = filters.tagsIds.length > 0 || filters.subscriptionPackageIds.length > 0 || filters.ribbonsIds.length > 0;

    const tagsOptions = useMemo(() => {
        if (isTagsDropdownDisabled) {
            return [];
        }

        const pkgOptions = (subscriptionPackages || [])
            .filter((item) => item.title.toLowerCase().trim().indexOf('enable login') === -1)
            .map((item) => ({ id: `${TagsSelectType.Package}-${item.id}`, title: item.title }));

        const tagOptions = (tags || []).map((tag) => ({ id: `${TagsSelectType.Tag}-${tag.id}`, title: tag.title }));

        const map = new Map<string | number, { id: string | number; title: string }>();

        const options = selectedCountryId === CountriesEnum.Usa ? [...pkgOptions, ...tagOptions] : [...tagOptions];

        options.forEach((opt) => {
            if (!map.has(opt.id)) map.set(opt.id, opt);
        });

        return Array.from(map.values()).sort((a, b) => {
            const at = a.title.toLowerCase();
            const bt = b.title.toLowerCase();
            if (at < bt) return -1;
            if (at > bt) return 1;
            return 0;
        });
    }, [subscriptionPackages, tags, isTagsDropdownDisabled]);

    const selectedTags = useMemo(() => {
        const selectedIds: (string | number)[] = [
            ...filters.subscriptionPackageIds.map((id) => `${TagsSelectType.Package}-${id}`),
            ...filters.tagsIds.map((id) => `${TagsSelectType.Tag}-${id}`)
        ];

        return selectedIds
            .map((encodedId) => {
                const [parsedType, parsedIdStr] = (encodedId as string).split('-');
                const parsedId = +parsedIdStr;

                if (!parsedId) return null;

                if (parsedType === TagsSelectType.Package) {
                    const pack = subscriptionPackages?.find((p) => p.id === parsedId);
                    if (pack) {
                        return { id: encodedId, title: pack.title };
                    }
                } else if (parsedType === TagsSelectType.Tag) {
                    const tag = tags?.find((t) => t.id === parsedId);
                    if (tag) {
                        return { id: encodedId, title: tag.title };
                    }
                }

                return null;
            })
            .filter(Boolean) as { id: string | number; title: string }[];
    }, [filters.subscriptionPackageIds, filters.tagsIds, subscriptionPackages, tags]);

    const handleRemoveTag = useCallback(
        (id: string | number) => {
            const [parsedType, parsedIdStr] = (id as string).split('-');
            const parsedId = +parsedIdStr;
            if (parsedId) {
                if (parsedType === TagsSelectType.Tag) {
                    const next = filters.tagsIds.filter((t) => t !== parsedId);
                    onChange({ ...filters, tagsIds: next });
                } else if (parsedType === TagsSelectType.Package) {
                    const next = filters.subscriptionPackageIds.filter((p) => p !== parsedId);
                    onChange({ ...filters, subscriptionPackageIds: next });
                }
            }
        },
        [filters, onChange]
    );

    const MAC_RIBBON_ID = useMemo(() => {
        return ribbons?.find((r) => r.title === MAC_CATEGORY_TITLE)?.id;
    }, [ribbons]);

    return (
        <div className="BrowseSection">
            <div className="BrowseSection__main">
                <span className="BrowseSection__label">Browse by:</span>

                <div className="BrowseSection__container">
                    <div className="BrowseSection__left">
                        <FiltersBar
                            ribbonOptions={ribbonOptions}
                            catLoad={catLoad}
                            tagsOptions={tagsOptions}
                            tagLoad={tagLoad}
                            packageLoad={packageLoad}
                            selectedTags={selectedTags}
                            filters={filters}
                            onChange={onChange}
                            localSearchQuery={localSearchQuery}
                            onSearchChange={handleSearchChange}
                            onSearchClear={handleSearchClear}
                            searchInputRef={searchInputRef}
                            onSearchBlur={handleSearchBlur}
                            hasFilters={hasFilters}
                            mode={mode}
                            getRem={getRem}
                            isTagsDropdownDisabled={isTagsDropdownDisabled}
                            macRibbonId={MAC_RIBBON_ID}
                        />
                    </div>

                    <div className="BrowseSection__right-controls">
                        <div className="BrowseSection__view-toggle">
                            <button
                                className={`BrowseSection__view-button ${mode === 'tile' ? 'active' : ''}`}
                                onClick={() => {
                                    if (mode !== 'tile') {
                                        setMode('tile');
                                        onViewModeChange?.('tile');
                                        const shouldResetTypes =
                                            selectedCountryId === CountriesEnum.Global &&
                                            filters.types.includes(DocumentTypesEnum.Mac);
                                        const shouldClearSearch = !!filters.searchQuery;
                                        if (shouldResetTypes || shouldClearSearch) {
                                            onChange({
                                                ...filters,
                                                ...(shouldClearSearch ? { searchQuery: '' } : {}),
                                                ...(shouldResetTypes ? { types: [DocumentTypesEnum.Publication] } : {})
                                            });
                                            if (shouldClearSearch) setLocalSearchQuery('');
                                        }
                                    }
                                }}
                            >
                                tile
                            </button>
                            <span className="BrowseSection__separator">|</span>
                            <button
                                className={`BrowseSection__view-button ${mode === 'table' ? 'active' : ''}`}
                                onClick={() => {
                                    if (mode !== 'table') {
                                        setMode('table');
                                        onViewModeChange?.('table');
                                    }
                                }}
                            >
                                table
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <TagsBar
                selectedTags={selectedTags}
                handleRemoveTag={handleRemoveTag}
                hasFilters={hasFilters}
                filters={filters}
                onChange={onChange}
                mode={mode}
                shouldShowFilteredResults={shouldShowFilteredResults}
                selectedSortType={selectedSortType}
                setSelectedSortType={setSelectedSortType}
                TYPE_OPTIONS={TYPE_OPTIONS}
            />
        </div>
    );
};

export default BrowseSection;
